/**
 * The advanced fabricator.
 *
 * Turns linelits back into line. No service charge — a linelit is a unit of
 * line that got cut too short to be one, so this is re-melting rather than
 * manufacturing, and there is nothing to mark up. What you pay is the material,
 * which is the material.
 *
 * Everything it makes is standard grade. Strength is what upgrades buy, and a
 * machine that could print composite would make the whole upgrade path
 * pointless.
 */
import { LINE_HEALTH } from '../ship/upgrades.js';
import { DEFAULT_LINE_COLOR } from '../ship/upgrades.js';
import { MIN_LINE_LENGTH } from '../ship/stock.js';
/** The longest single run the machine can lay down. */
export const FABRICATOR_MAX_LENGTH = 50;
/**
 * Linelits per unit of line.
 *
 * One to one, because that is what a linelit *is*: an offcut under
 * `MIN_LINE_LENGTH`, valued at its own length.
 *
 * This is the *reference* rate the whole economy is priced against. A trader
 * sells at `TRADER_MARKUP` over it, which is what stops buying-and-melting
 * being an infinite money press — and what makes a fabricator the better deal
 * for anyone who has linelits and is not in a hurry.
 */
export const LINELITS_PER_UNIT = 1;
/** Standard grade, because strength is what upgrades are for. */
export const FABRICATED_STRENGTH = 1;
/** What a run of this length costs. */
export function fabricationCost(length) {
    return Math.max(0, length) * LINELITS_PER_UNIT;
}
/**
 * Lay down a line, if the length is one the machine makes and you can pay.
 *
 * Whole units only. The machine is fed by the unit and the editor snaps to a
 * quarter-unit grid, so offering fractional runs would let a player buy a
 * length they can place but never cut cleanly back out of.
 */
export function fabricate(length, carried) {
    const cost = fabricationCost(length);
    const refuse = (refused) => ({
        ok: false,
        refused,
        cost,
        line: null,
    });
    if (!Number.isInteger(length))
        return refuse('notWhole');
    if (length < MIN_LINE_LENGTH)
        return refuse('tooShort');
    if (length > FABRICATOR_MAX_LENGTH)
        return refuse('tooLong');
    if (carried + 1e-6 < cost)
        return refuse('cannotAfford');
    return {
        ok: true,
        refused: null,
        cost,
        line: {
            id: `fab-${length}-${Math.random().toString(36).slice(2, 8)}`,
            length,
            health: LINE_HEALTH,
            strength: FABRICATED_STRENGTH,
            color: DEFAULT_LINE_COLOR,
        },
    };
}
/**
 * What a line is worth melted down.
 *
 * The same rate in the other direction, so the machine is a conversion rather
 * than a sink. Health is ignored: you are recovering material, and a bent
 * length of line weighs what it weighs.
 */
export function meltValue(length) {
    return fabricationCost(length);
}
//# sourceMappingURL=fabricator.js.map